/**
 * ============================================================================
 * TRANSACTION DETAILS COMPONENT
 * ============================================================================
 * 
 * Full transaction view with all details and actions
 */

/**
 * ============================================================================
 * TRANSACTION DETAILS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Full transaction view with all details and actions
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTransaction } from '../../hooks';
import { useAuthContext } from '../../providers';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import TransactionTimeline from './TransactionTimeline';
import { getStatusLabel } from '../../utils/transactionStatus';
import Spinner from '../common/Spinner';

const STATUS_ICONS = {
  created: '📝',
  link_sent: '🔗',
  payment_pending: '⏳',
  escrow_funded: '💰',
  in_progress: '⚙️',
  delivered: '📦',
  disputed: '⚠️',
  completed: '✅',
  cancelled: '❌',
  refunded: '↩️',
};
import Avatar from '../common/Avatar';
import AutoReleaseCountdown from '../common/AutoReleaseCountdown';

/**
 * Transaction Details Component
 * @returns {JSX.Element}
 */
const TransactionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { getTransaction, markAsDelivered, approveDelivery, loading } = useTransaction();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [transaction, setTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'deliver' | 'approve' | 'dispute' | null

  useEffect(() => {
    const loadTransaction = async () => {
      try {
        setIsLoading(true);
        const response = await getTransaction(id);
        // Handle backend response structure: { success: true, data: { transaction: {...} } }
        const transactionData = response?.data?.transaction || response?.data || response;
        setTransaction(transactionData);
      } catch (error) {
        showError(language === 'fr' ? 'Échec du chargement de la transaction' : 'Failed to load transaction');
        console.error('Load transaction error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadTransaction();
    }
  }, [id, getTransaction, showError, language]);

  // Backend uses creatorId/counterpartyId and creatorRole/counterpartyRole
  const userId = user?.id;
  const isCreator = transaction?.creatorId === userId;
  const isCounterparty = transaction?.counterpartyId === userId;
  const isSeller = (isCreator && transaction?.creatorRole === 'seller') || 
                   (isCounterparty && transaction?.counterpartyRole === 'seller');
  const isBuyer = (isCreator && transaction?.creatorRole === 'buyer') || 
                  (isCounterparty && transaction?.counterpartyRole === 'buyer');
  
  // Backend status: 'escrow_funded', 'in_progress', 'delivered', 'completed'
  const canDeliver = isSeller && ['escrow_funded', 'in_progress'].includes(transaction?.status);
  const canApprove = isBuyer && transaction?.status === 'delivered';
  const canDispute = isBuyer && ['escrow_funded', 'in_progress', 'delivered'].includes(transaction?.status);

  const handleMarkDelivered = async () => {
    setActionLoading('deliver');
    try {
      await markAsDelivered(id);
      showSuccess(language === 'fr' ? 'Transaction marquée comme livrée!' : 'Transaction marked as delivered!');
      await loadTransaction();
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec du marquage comme livré' : 'Failed to mark as delivered'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async () => {
    setActionLoading('approve');
    try {
      await approveDelivery(id);
      showSuccess(language === 'fr' ? 'Paiement libéré !' : 'Payment released!');
      await loadTransaction();
    } catch (error) {
      showError(error.message || (language === 'fr' ? 'Échec de la libération du paiement' : 'Failed to approve delivery'));
    } finally {
      setActionLoading(null);
    }
  };

  const loadTransaction = async () => {
    try {
      setIsLoading(true);
      const response = await getTransaction(id);
      const transactionData = response?.data?.transaction || response?.data || response;
      setTransaction(transactionData);
    } catch (error) {
      showError('Failed to load transaction');
      console.error('Load transaction error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || loading) {
    return (
      <div className="tx-detail-loading">
        <Spinner size="lg" />
        <p className="dashboard-wt-card-desc mt-3">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="dashboard-wt-card tx-detail-not-found">
        <h2 className="dashboard-wt-card-title mb-2">{language === 'fr' ? 'Transaction introuvable' : 'Transaction Not Found'}</h2>
        <p className="dashboard-wt-card-desc mb-4">{language === 'fr' ? 'Cette transaction n\'existe pas ou vous n\'y avez pas accès.' : 'This transaction does not exist or you do not have access.'}</p>
        <button type="button" className="dashboard-wt-btn-primary" onClick={() => navigate('/dashboard')}>
          {language === 'fr' ? 'Aller au tableau de bord' : 'Go to Dashboard'}
        </button>
      </div>
    );
  }

  const shortCode = transaction.shortCode || (transaction.shortLink && transaction.shortLink.split('/').pop());
  const refLabel = shortCode || transaction.referenceId || transaction.id;

  return (
    <div className="tx-detail-content">
      {/* Header row */}
      <div className="tx-detail-header">
        <div>
          <h2 className="dashboard-wt-card-title mb-2">
            {language === 'fr' ? 'Transaction' : 'Transaction'} #{refLabel}
          </h2>
          <div className="flex flex-wrap items-center gap-2 gap-y-2">
            <span className="tx-detail-badge tx-detail-status">
              {STATUS_ICONS[transaction.status] || '📊'} {getStatusLabel(transaction.status)}
            </span>
            {(transaction.deliveryDays ?? transaction.calculated?.daysRemaining) != null && transaction.status === 'escrow_funded' && (
              <span className="tx-detail-badge">
                ⏱️ {transaction.deliveryDays ?? transaction.calculated?.daysRemaining} {language === 'fr' ? 'jours restants' : 'days remaining'}
              </span>
            )}
          </div>
        </div>
        {shortCode && (
          <div className="tx-detail-copy-wrap flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={linkCopied ? 'dashboard-wt-btn-secondary' : 'dashboard-wt-btn-primary'}
              onClick={async () => {
                const fullUrl = transaction.fullLink || `${window.location.origin}/t/${shortCode}`;
                try {
                  await navigator.clipboard.writeText(fullUrl);
                  setLinkCopied(true);
                  showSuccess(language === 'fr' ? 'Lien copié ! Envoyez-le à l\'acheteur par WhatsApp, SMS, etc.' : 'Link copied! Send it to the buyer via WhatsApp, text, etc.');
                  setTimeout(() => setLinkCopied(false), 2500);
                } catch {
                  showError(language === 'fr' ? 'Impossible de copier le lien' : 'Could not copy link');
                }
              }}
            >
              {linkCopied ? '✓ ' + (language === 'fr' ? 'Copié !' : 'Copied!') : '📋 ' + (language === 'fr' ? 'Copier le lien de paiement' : 'Copy payment link')}
            </button>
            <a
              href={`/t/${shortCode}`}
              target="_blank"
              rel="noopener noreferrer"
              className="dashboard-wt-btn-secondary"
            >
              🔗 {language === 'fr' ? 'Ouvrir l’aperçu' : 'Open preview'}
            </a>
          </div>
        )}
      </div>

      <div className="tx-detail-grid">
        {/* Main */}
        <div className="tx-detail-main">
          {/* Transaction Info */}
          <div className="dashboard-wt-card tx-detail-card">
            <div className="tx-detail-card-head">
              <span className="tx-detail-icon" aria-hidden>📦</span>
              <div>
                <h3 className="dashboard-wt-card-title">
                  {transaction.title || (language === 'fr' ? 'Transaction sans titre' : 'Untitled Transaction')}
                </h3>
                {transaction.description && (
                  <p className="dashboard-wt-card-desc line-clamp-2 mt-1">{transaction.description}</p>
                )}
              </div>
            </div>
            <div className="tx-detail-amount">
              <span className="dashboard-wt-card-desc">{language === 'fr' ? 'Montant:' : 'Amount:'}</span>
              <span className="tx-detail-amount-value">{formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}</span>
            </div>
            {transaction.description && (
              <div className="dashboard-wt-card-footer">
                <span className="dashboard-wt-card-desc">{language === 'fr' ? 'Description:' : 'Description:'}</span>
                <p className="dashboard-wt-card-content mt-1 whitespace-pre-wrap">{transaction.description}</p>
              </div>
            )}
            <div className="tx-detail-meta">
              {(transaction.deliveryDeadline || transaction.deliveryDate) && (
                <div>
                  <span className="dashboard-wt-card-desc">{language === 'fr' ? 'Date de livraison:' : 'Delivery Date:'}</span>
                  <p className="dashboard-wt-card-content mt-0.5">{formatDate(transaction.deliveryDeadline || transaction.deliveryDate)}</p>
                </div>
              )}
              {(transaction.deliveryDays ?? transaction.calculated?.daysRemaining) != null && (
                <div>
                  <span className="dashboard-wt-card-desc">{language === 'fr' ? 'Délai:' : 'Delivery Time:'}</span>
                  <p className="dashboard-wt-card-content mt-0.5">{transaction.deliveryDays ?? transaction.calculated?.daysRemaining} {language === 'fr' ? 'jours' : 'days'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="dashboard-wt-card tx-detail-card">
            <h3 className="dashboard-wt-card-title mb-3 flex items-center gap-2">
              <span>📅</span> {language === 'fr' ? 'Chronologie' : 'Timeline'}
            </h3>
            <TransactionTimeline transaction={transaction} />
          </div>

          {/* Deliverables */}
          {Array.isArray(transaction.deliverablesUrl) && transaction.deliverablesUrl.length > 0 && (
            <div className="dashboard-wt-card tx-detail-card">
              <h3 className="dashboard-wt-card-title mb-3 flex items-center gap-2">
                <span>📎</span> {language === 'fr' ? 'Livrables' : 'Deliverables'}
              </h3>
              <div className="space-y-2">
                {transaction.deliverablesUrl.map((url, index) => (
                  <button
                    key={index}
                    type="button"
                    className="tx-detail-deliverable"
                    onClick={() => window.open(url, '_blank')}
                  >
                    <span>📎</span>
                    <span className="flex-1 text-left">{url.split('/').pop() || (language === 'fr' ? 'Fichier' : 'File') + ' ' + (index + 1)}</span>
                    <span aria-hidden>↗</span>
                  </button>
                ))}
              </div>
              {transaction.deliverablesNotes && (
                <p className="dashboard-wt-card-desc mt-3 whitespace-pre-wrap">{transaction.deliverablesNotes}</p>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="tx-detail-sidebar">
          {isBuyer && (
            <div className="dashboard-wt-card tx-detail-card">
              <h3 className="dashboard-wt-card-title mb-3 flex items-center gap-2">👨‍💼 {language === 'fr' ? 'Vendeur' : 'Seller'}</h3>
              <div className="flex items-center gap-3">
                <Avatar
                  name={(transaction.creator?.fullName || transaction.creator?.full_name || transaction.creator?.name) || 'U'}
                  size="md"
                />
                <div>
                  <div className="dashboard-wt-card-content font-semibold">
                    {transaction.creator?.fullName || transaction.creator?.full_name || transaction.creator?.name || (language === 'fr' ? 'Vendeur' : 'Seller')}
                  </div>
                  {transaction.creator?.email && (
                    <div className="dashboard-wt-card-desc text-sm mt-0.5">{transaction.creator.email}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="dashboard-wt-card tx-detail-card">
            <h3 className="dashboard-wt-card-title mb-3">{language === 'fr' ? 'Actions' : 'Actions'}</h3>
            <div className="space-y-2">
              {canDeliver && (
                <button
                  type="button"
                  className="dashboard-wt-btn-primary w-full"
                  disabled={!!actionLoading}
                  onClick={() => {
                    setActionLoading('deliver');
                    navigate(`/transactions/${id}/deliver`);
                  }}
                >
                  {actionLoading === 'deliver' ? (language === 'fr' ? 'Ouverture...' : 'Opening...') : (language === 'fr' ? 'Marquer comme livré' : 'Mark as Delivered') + ' →'}
                </button>
              )}
              {canApprove && (
                <>
                  {transaction.deliveredAt && (
                    <div className="mb-3">
                      <AutoReleaseCountdown deliveredAt={transaction.deliveredAt} onExpire={() => loadTransaction()} />
                    </div>
                  )}
                  <button
                    type="button"
                    className="dashboard-wt-btn-primary w-full"
                    disabled={!!actionLoading}
                    onClick={handleApprove}
                  >
                    {actionLoading === 'approve' ? (language === 'fr' ? 'Libération...' : 'Releasing...') : '✓ ' + (language === 'fr' ? 'Libérer le paiement' : 'Release Payment')}
                  </button>
                  <button
                    type="button"
                    className="dashboard-wt-btn-secondary w-full"
                    disabled={!!actionLoading}
                    onClick={() => {
                      setActionLoading('dispute');
                      navigate(`/disputes/new?transactionId=${id}`);
                    }}
                  >
                    {actionLoading === 'dispute' ? (language === 'fr' ? 'Ouverture...' : 'Opening...') : '⚠ ' + (language === 'fr' ? 'Soulever un problème' : 'Raise Issue')}
                  </button>
                </>
              )}
              {canDispute && !canApprove && (
                <button
                  type="button"
                  className="dashboard-wt-btn-secondary w-full"
                  disabled={!!actionLoading}
                  onClick={() => {
                    setActionLoading('dispute');
                    navigate(`/disputes/new?transactionId=${id}`);
                  }}
                >
                  {actionLoading === 'dispute' ? (language === 'fr' ? 'Ouverture...' : 'Opening...') : (language === 'fr' ? 'Soulever un problème' : 'Raise Issue')}
                </button>
              )}
              {!canDeliver && !canApprove && !canDispute && (
                <p className="dashboard-wt-card-desc text-sm py-2" role="status">
                  {language === 'fr' ? 'Aucune action disponible pour ce statut.' : 'No actions available for this status.'}
                </p>
              )}
            </div>
          </div>

          {isBuyer && transaction.status !== 'completed' && (
            <div className="dashboard-wt-card tx-detail-card tx-detail-protection">
              <h4 className="dashboard-wt-card-title mb-2 flex items-center gap-2">🔒 {language === 'fr' ? 'Votre argent est en sécurité' : 'Your money is safe'}</h4>
              <p className="dashboard-wt-card-desc text-sm leading-relaxed">
                {language === 'fr' ? 'Les fonds sont en escrow jusqu\'à confirmation. Vous pouvez approuver ou soulever un problème après livraison.' : 'Funds are held in escrow until delivery is confirmed. You can approve or raise an issue after delivery.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;
